import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (token: string, refreshToken: string, user: User) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    // Load user from localStorage synchronously on mount to prevent initial redirect
    try {
      const storedUser = localStorage.getItem('admin_user');
      const token = localStorage.getItem('admin_auth_token');

      if (storedUser && token && storedUser !== 'undefined' && storedUser !== 'null') {
        const parsed = JSON.parse(storedUser);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error parsing stored auth data:', error);
    }

    // If we reach here, data was invalid, so clean up
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_auth_token');
    localStorage.removeItem('admin_refresh_token');
    return null;
  });

  useEffect(() => {
    // Setup or additional syncing can go here if needed
  }, []);

  const login = async (token: string, refreshToken: string, userData: User) => {
    localStorage.setItem('admin_auth_token', token);
    localStorage.setItem('admin_refresh_token', refreshToken);
    localStorage.setItem('admin_user', JSON.stringify(userData));
    setUser(userData);

    // Return a promise that resolves after state is updated
    return new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 0);
    });
  };

  const logout = () => {
    localStorage.removeItem('admin_auth_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
